import type { ApiErrorBody, Session } from "./types";

/**
 * The dashboard's HTTP client.
 *
 * It owns three responsibilities that would otherwise be scattered across every
 * component: attaching the session token, turning an error response into a typed
 * exception, and transparently refreshing an expired session exactly once.
 */

/** ApiError carries the platform's machine-readable error code, so a component can
 *  branch on the code rather than matching on a human message that may change. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: { field: string; message: string }[];
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    const message = body?.error?.message ?? fallback;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error?.code ?? "unknown_error";
    this.fields = body?.error?.fields ?? [];
    this.requestId = body?.error?.request_id;
  }

  /** fieldError returns the message for one field, for inline form validation. */
  fieldError(field: string): string | undefined {
    return this.fields.find((f) => f.field === field)?.message;
  }
}

/**
 * API_BASE is where the dashboard sends its requests.
 *
 * Empty means same-origin, which is what local development uses: Vite proxies /api/
 * through to the Go process, so the browser never makes a cross-origin request and the
 * CORS rules exercised in development match those in production.
 *
 * In production the dashboard is a static bundle on Cloudflare Pages and the API is on
 * a VPS, so they are different origins and this must be set. It is baked in at build
 * time, which is why UAT and production are separate builds — see deploy/README.md.
 *
 * The trailing slash is stripped so callers can keep writing "/api/dashboard/...".
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

/** apiURL resolves a path against the configured API origin. */
export function apiURL(path: string): string {
  return API_BASE + path;
}

const STORAGE_KEY = "payflow.session";

/** Session storage lives in localStorage. A dashboard token is short-lived and the
 *  refresh token rotates on every use, so a stolen copy has a narrow window and is
 *  detectable when replayed. */
export const sessionStore = {
  read(): Session | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      // A private window or blocked site data leaves the user signed out, which is
      // the correct outcome rather than an unhandled exception at start-up.
      return null;
    }
  },
  write(session: Session) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      /* Storage is unavailable; the session simply will not survive a reload. */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

/** onUnauthenticated is invoked when a session cannot be recovered, so the app can
 *  route back to sign-in from anywhere without every caller handling it. */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** formData bypasses JSON encoding, for KYC document uploads. */
  formData?: FormData;
  /** raw returns the Response untouched, for file downloads. */
  raw?: boolean;
  headers?: Record<string, string>;
  /** skipAuth is used by sign-in and registration, which have no session yet. */
  skipAuth?: boolean;
}

/** refreshInFlight de-duplicates concurrent refreshes: several requests failing at
 *  once must produce one refresh, not one per request, or the rotation chain breaks
 *  and every session is revoked as a suspected replay. */
let refreshInFlight: Promise<Session | null> | null = null;

async function refreshSession(): Promise<Session | null> {
  const current = sessionStore.read();
  if (!current?.refresh_token) return null;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(apiURL("/api/dashboard/auth/refresh"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: current.refresh_token }),
        });
        if (!response.ok) {
          sessionStore.clear();
          return null;
        }
        const session = (await response.json()) as Session;
        sessionStore.write(session);
        return session;
      } catch {
        return null;
      } finally {
        // Cleared on the next tick so callers awaiting this promise all see the
        // same result before a new refresh can start.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

async function send(path: string, options: RequestOptions, token?: string): Promise<Response> {
  const headers: Record<string, string> = { ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData; // the browser sets the multipart boundary itself
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  return fetch(apiURL(path), { method: options.method ?? "GET", headers, body });
}

/**
 * request performs an authenticated API call.
 *
 * A 401 triggers exactly one refresh-and-retry. If that also fails the session is
 * cleared and the app is told to return to sign-in.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = options.skipAuth ? null : sessionStore.read();
  let response = await send(path, options, session?.access_token);

  if (response.status === 401 && !options.skipAuth && session) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send(path, options, refreshed.access_token);
    } else {
      sessionStore.clear();
      onUnauthenticated?.();
      throw new ApiError(401, null, "Your session has ended. Please sign in again.");
    }
  }

  if (options.raw) {
    if (!response.ok) throw new ApiError(response.status, null, "The download failed.");
    return response as unknown as T;
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: "POST", formData }),
  postPublic: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body, skipAuth: true }),
};

/** query builds a query string, omitting empty values so the URL stays readable. */
export function query(params: Record<string, string | number | boolean | undefined | string[]>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => v && search.append(key, v));
      continue;
    }
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}
